/**
 * Mixin to handle touch events on a spacer element and emit 3D rotation events.
 * Locks to one axis (horizontal or vertical) after a threshold is reached.
 */
export default {
  data() {
    return {
      _spacerTouch: null,
    };
  },
  mounted() {
    // Add touchmove listener with passive: false to allow preventDefault
    this.$nextTick(() => {
      if (this.$refs.spacer) {
        this.$refs.spacer.addEventListener(
          'touchmove',
          this.onSpacerTouchMove,
          { passive: false }
        );
      }
    });
  },
  beforeDestroy() {
    if (this.$refs.spacer) {
      this.$refs.spacer.removeEventListener(
        'touchmove',
        this.onSpacerTouchMove
      );
    }
  },
  methods: {
    /**
     * Handle touchstart on spacer
     * @param {TouchEvent} event
     */
    onSpacerTouchStart(event) {
      const touch = event.touches[0];
      this._spacerTouch = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        totalDeltaX: 0,
        totalDeltaY: 0,
        axis: null, // Will be 'horizontal' or 'vertical' once determined
      };
    },

    /**
     * Handle touchmove on spacer
     * Determines dominant axis and locks to it
     * Emits 'rotate-3d-model' event for horizontal movement
     * @param {TouchEvent} event
     */
    onSpacerTouchMove(event) {
      if (!this._spacerTouch) return;

      const touch = event.touches[0];
      const deltaX = touch.clientX - this._spacerTouch.lastX;
      const deltaY = touch.clientY - this._spacerTouch.startY;
      this._spacerTouch.lastX = touch.clientX;

      // Accumulate total movement if axis not yet determined
      if (!this._spacerTouch.axis) {
        this._spacerTouch.totalDeltaX += Math.abs(deltaX);
        this._spacerTouch.totalDeltaY = Math.abs(deltaY);

        // Threshold to determine axis (10px of movement)
        const threshold = 10;
        const total =
          this._spacerTouch.totalDeltaX + this._spacerTouch.totalDeltaY;

        if (total >= threshold) {
          // Determine dominant axis
          if (this._spacerTouch.totalDeltaX > this._spacerTouch.totalDeltaY) {
            this._spacerTouch.axis = 'horizontal';
          } else {
            this._spacerTouch.axis = 'vertical';
          }
        }
      }

      // Act based on locked axis
      if (this._spacerTouch.axis === 'horizontal') {
        // Prevent vertical scroll, rotate 3D model
        event.preventDefault();
        // Only emit if movement is significant (> 0.5px) to reduce event frequency
        if (Math.abs(deltaX) > 0.5) {
          this.$root.$emit('rotate-3d-model', deltaX);
        }
      }
      // If vertical or not yet determined, let native scroll happen
    },

    /**
     * Handle touchend on spacer
     */
    onSpacerTouchEnd() {
      this._spacerTouch = null;
    },
  },
};
